import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { TableVirtuoso } from 'react-virtuoso';
import { FileText, Search, X, Download, Printer, Ban, Info, MessageCircle, Loader2, Shield, Plus, Minus, RefreshCw, FileX2, ChevronDown, CreditCard, Receipt, Undo2, FileUp, Eraser, Copy } from 'lucide-react';
import { api, ApiError, getToken } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { useToast } from '../../components/Toast';
import { formatMoeda, formatCpfCnpj, formatDataHoraSP, parseSPDate, PRAZO_CANCELAMENTO_MS } from '@fiodecobre/shared';
import { ModalDetalhesNota } from '../../components/notas-fiscais/ModalDetalhesNota';
import { ModalCancelarNota } from '../../components/notas-fiscais/ModalCancelarNota';
import { ModalDevolverNota } from '../../components/notas-fiscais/ModalDevolverNota';
import { ModalResultadoDevolucao, type ResultadoDevolucao } from '../../components/notas-fiscais/ModalResultadoDevolucao';
import { ModalConsultaSefaz, type ConsultaSefazData } from '../../components/notas-fiscais/ModalConsultaSefaz';
import { ModalEnviarWhatsAppNF } from '../../components/notas-fiscais/ModalEnviarWhatsAppNF';
import { ModalInutilizarNumeracao } from '../../components/notas-fiscais/ModalInutilizarNumeracao';
import { ModalComprovanteVenda, type ComprovanteVendaInfo } from '../../components/notas-fiscais/ModalComprovanteVenda';
import { ProblemasNotasLista } from './ProblemasNotasLista';
import { InutilizacoesTab } from '../configuracoes/InutilizacoesTab';
import { DuplicadasTab } from './DuplicadasTab';
import { FotoProduto } from '../../components/FotoProduto';

type Tipo = 'nfce' | 'nfe';
type Aba = Tipo | 'problemas' | 'devolucoes' | 'inutilizacoes' | 'duplicadas';

export interface NotaFiscalRow {
  id: number;
  chave: string | null;
  numero: number;
  serie: string;
  ambiente: number;
  status: 'autorizada' | 'cancelada' | 'rejeitada' | 'rejeitada_reusada' | 'erro_comunicacao';
  protocolo: string | null;
  codigo_status: string | null;
  motivo_status: string | null;
  // [DA-488] Causa CRUA da falha de transporte na emissao (ECONNRESET/timeout). Fica a
  // parte porque a reconciliacao sobrescreve motivo_status com a resposta da SEFAZ.
  erro_comunicacao_msg?: string | null;
  valor_total: string;
  emitido_em: string;
  cancelado_em: string | null;
  motivo_cancelamento: string | null;
  qr_code_url: string | null;
  orcamento_id: number | null;
  orcamento_numero: string | null;
  cliente_id: number | null;
  cliente_codigo: string | null;
  cliente_nome: string | null;
  cliente_razao: string | null;
  cliente_doc: string | null;
  cliente_whatsapp: string | null;
  vendedor_nome: string | null;
  forma_pagamento: string | null;
  // 'novo' = nota emitida pelo sistema atual (serie 020). 'legacy' = ControleFC antigo (serie 010).
  // Notas legacy sao apenas leitura — endpoints de DANFE/cancelar/WhatsApp/SEFAZ nao funcionam pra elas.
  origem?: 'novo' | 'legacy';
  // Devolucao fiscal. tipo_nota='devolucao' = a propria nota e uma devolucao (entrada,
  // sempre mod 55). ja_devolvida = esta nota de venda ja foi desfeita por uma devolucao
  // autorizada. ref_chave = chave da nota original (quando tipo_nota='devolucao').
  tipo_nota?: 'venda' | 'devolucao';
  ref_chave?: string | null;
  nfe_origem_id?: number | null;
  // Origem NFC-e ([DA-440]) — exclusiva com nfe_origem_id.
  nfce_origem_id?: number | null;
  // Origem NOTA DE ENTRADA ([DA-559]) — devolucao de COMPRA ao fornecedor. Esta e a unica
  // devolucao de SAIDA: a mercadoria vai embora. Nao tem orcamento nem cliente; quem identifica
  // a linha e' `fornecedor_nome`.
  nota_entrada_origem_id?: number | null;
  fornecedor_nome?: string | null;
  ref_modelo?: 'nfe' | 'nfce' | 'nota_entrada' | null;
  ja_devolvida?: boolean;
  // ref_numero = numero da nota original (quando esta linha e uma devolucao). Vem como TEXTO —
  // o numero da nota de entrada e' varchar no banco (sai do XML do fornecedor).
  // devolucao_numero = numero da devolucao autorizada (quando esta venda ja foi devolvida).
  ref_numero?: number | string | null;
  devolucao_numero?: number | null;
}

interface OrcamentoItem {
  id: number;
  produto_id: number;
  produto_descricao: string;
  codigo_interno: string | null;
  unidade_sigla: string | null;
  foto_url: string | null;
  quantidade: string;
  preco_unitario: string;
  desconto_percentual: string;
  subtotal: string;
}

const STATUS_LABEL: Record<string, { label: string; cls: string }> = {
  autorizada: { label: 'Autorizada', cls: 'badge-verde' },
  cancelada: { label: 'Cancelada', cls: 'badge-vermelho' },
  rejeitada: { label: 'Rejeitada', cls: 'badge-amarelo' },
  // Rejeicao de schema cujo numero foi devolvido ao pool e reusado pela emissao
  // seguinte (DA-180) — fica no historico, mas nao e buraco nem pendencia.
  rejeitada_reusada: { label: 'Reaproveitada', cls: 'badge-cinza' },
  erro_comunicacao: { label: 'Erro', cls: 'badge-cinza' },
};

function hoje(): string {
  return new Date().toISOString().slice(0, 10);
}
function diasAtras(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}

async function baixarBlobAutenticado(path: string): Promise<Blob> {
  const token = getToken();
  const r = await fetch(path, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!r.ok) {
    let msg = String(r.status);
    try {
      const j = (await r.clone().json()) as { erro?: string };
      if (j?.erro) msg = j.erro;
    } catch {
      try { msg = await r.text(); } catch {}
    }
    throw new Error(msg);
  }
  return r.blob();
}

export function NotasFiscaisLista() {
  const { usuario } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const podeCancelar = usuario?.perfil === 'admin' || usuario?.perfil === 'gerente';
  const ehAdmin = usuario?.perfil === 'admin' || usuario?.perfil === 'gerente';
  const [modalInutilizarAberto, setModalInutilizarAberto] = useState(false);

  const [searchParams] = useSearchParams();
  const abaParam = searchParams.get('aba');
  const abaDaUrl: Aba | null = abaParam === 'problemas' ? 'problemas'
    : abaParam === 'devolucoes' ? 'devolucoes'
    : abaParam === 'inutilizacoes' && ehAdmin ? 'inutilizacoes'
    : abaParam === 'duplicadas' && ehAdmin ? 'duplicadas'
    : null;
  const [aba, setAba] = useState<Aba>(abaDaUrl ?? 'nfce');
  // A faixa de alerta navega pra /notas-fiscais?aba=X. Se o usuario JA estiver nesta tela,
  // o componente nao remonta e o estado inicial acima nunca roda de novo — a URL mudava e a
  // aba ficava onde estava. Este efeito obedece a querystring quando ela muda; trocar de aba
  // pela mao nao mexe no parametro, entao nao ha briga entre os dois.
  useEffect(() => {
    if (abaDaUrl) setAba(abaDaUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [abaParam]);
  const [qtdProblemas, setQtdProblemas] = useState<number>(0);
  const [qtdDuplicadas, setQtdDuplicadas] = useState<number>(0);
  // Devolucoes sao NF-e (mod 55), entao usam os endpoints /api/nfe (DANFE/XML/SEFAZ/cancelar).
  const abaNotas: Tipo = aba === 'nfe' || aba === 'devolucoes' ? 'nfe' : 'nfce';
  const [lista, setLista] = useState<NotaFiscalRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [dataInicio, setDataInicio] = useState<string>(diasAtras(7));
  const [dataFim, setDataFim] = useState<string>(hoje());
  const [status, setStatus] = useState<string>('');
  const [numero, setNumero] = useState<string>('');
  const [chave, setChave] = useState<string>('');

  const [modalDetalhes, setModalDetalhes] = useState<NotaFiscalRow | null>(null);
  const [modalCancelar, setModalCancelar] = useState<NotaFiscalRow | null>(null);
  const [modalConsulta, setModalConsulta] = useState<{ nota: NotaFiscalRow; data: ConsultaSefazData; tipoLabel: string } | null>(null);
  const [modalWhatsapp, setModalWhatsapp] = useState<NotaFiscalRow | null>(null);
  const [modalDevolver, setModalDevolver] = useState<NotaFiscalRow | null>(null);
  const [resultadoDevolucao, setResultadoDevolucao] = useState<
    { resultado: ResultadoDevolucao; origemRotulo: 'NFC-e' | 'NF-e'; origemNumero: number } | null
  >(null);

  const [preparandoDanfe, setPreparandoDanfe] = useState<number | null>(null);
  // menu de escolha de formato do cupom (NFC-e): ancorado no caret de cada linha
  const [menuFormato, setMenuFormato] = useState<{ nota: NotaFiscalRow; rect: DOMRect } | null>(null);
  const [comprovanteInfo, setComprovanteInfo] = useState<ComprovanteVendaInfo | null>(null);
  const [preparandoPdf, setPreparandoPdf] = useState<number | null>(null);
  const [consultando, setConsultando] = useState<number | null>(null);
  const [trocando, setTrocando] = useState<number | null>(null);

  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [itensCache, setItensCache] = useState<Record<number, OrcamentoItem[]>>({});
  const [loadingItens, setLoadingItens] = useState<number | null>(null);

  // Refs espelhando o estado pra usar dentro de toggleExpand sem virar dep do useCallback
  // (manter callback estavel preserva o memo da NotaFiscalRow). React 18 deferiu o updater
  // funcional pro proximo render, entao ler "side-effect" la dentro NAO funciona.
  const expandedIdRef = useRef<number | null>(null);
  const itensCacheRef = useRef<Record<number, OrcamentoItem[]>>({});
  useEffect(() => { expandedIdRef.current = expandedId; }, [expandedId]);
  useEffect(() => { itensCacheRef.current = itensCache; }, [itensCache]);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const carregar = useCallback(async () => {
    if (aba === 'problemas' || aba === 'inutilizacoes' || aba === 'duplicadas') return; // esses cuidam do proprio fetch
    setLoading(true);
    try {
      const query: Record<string, string> = {};
      if (aba === 'devolucoes') {
        // Devolucoes sao raras → mostrar TODAS, sem janela de data (a aba nao tem filtros).
        query.tipo_nota = 'devolucao';
      } else {
        if (dataInicio) query.data_inicio = dataInicio;
        if (dataFim) query.data_fim = dataFim;
        if (status) query.status = status;
        if (numero) query.numero = numero;
        if (chave) query.chave = chave;
      }
      const data = await api.get<NotaFiscalRow[]>(`/api/${abaNotas}`, { query });
      setLista(data);
      setExpandedId(null);
      setItensCache({});
    } catch (e) {
      toast.erro((e as Error).message);
      setLista([]);
    } finally {
      setLoading(false);
    }
  }, [aba, dataInicio, dataFim, status, numero, chave, toast]);

  // Polling do contador de problemas pra mostrar badge na aba
  useEffect(() => {
    let cancelado = false;
    async function tick() {
      try {
        const r = await api.get<{ total: number }>('/api/notas-fiscais/problemas-resumo');
        if (!cancelado) setQtdProblemas(r.total ?? 0);
      } catch {}
    }
    void tick();
    const id = setInterval(tick, 5 * 60 * 1000);
    return () => { cancelado = true; clearInterval(id); };
  }, []);

  // Selo da aba "Duplicadas". Conta SO o que ainda nao tem ciencia — se contasse os
  // casos ja vistos, o numero nunca voltaria a zero e viraria paisagem (a aba nasce
  // com 3 historicos que o Ruan decidiu nao mexer). Restrito a admin/gerente, igual
  // a aba; pro caixa o endpoint responderia 403.
  useEffect(() => {
    if (!ehAdmin) return;
    let cancelado = false;
    async function tick() {
      try {
        const r = await api.get<{ total: number }>('/api/notas-fiscais/duplicadas-resumo');
        if (!cancelado) setQtdDuplicadas(r.total ?? 0);
      } catch {}
    }
    void tick();
    const id = setInterval(tick, 5 * 60 * 1000);
    return () => { cancelado = true; clearInterval(id); };
  }, [ehAdmin]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => { void carregar(); }, 250);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [carregar]);

  function limparFiltros() {
    setDataInicio(diasAtras(7));
    setDataFim(hoje());
    setStatus('');
    setNumero('');
    setChave('');
  }

  // formato so se aplica a NFC-e (cupom): 'cartao' = bobina 5,5cm, 'tradicional' = 80mm.
  const verDanfe = useCallback(async (nota: NotaFiscalRow, formato: 'cartao' | 'tradicional' = 'cartao') => {
    setPreparandoDanfe(nota.id);
    try {
      const endpoint = abaNotas === 'nfce' ? 'danfce' : 'danfe';
      const qs = abaNotas === 'nfce' ? `?formato=${formato}` : '';
      const blob = await baixarBlobAutenticado(`/api/${abaNotas}/${nota.id}/${endpoint}${qs}`);
      const url = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
      window.open(url, '_blank');
    } catch (e) {
      toast.erro('Erro ao gerar DANFE: ' + (e as Error).message);
    } finally {
      setPreparandoDanfe(null);
    }
  }, [aba, toast]);

  // Baixa a nota como ARQUIVO PDF (DANFE na NF-e / cupom na NFC-e) pra encaminhar ao cliente.
  // NFC-e usa o formato 'tradicional' (80mm) por ser o cupom mais legivel como documento avulso.
  const baixarPdf = useCallback(async (nota: NotaFiscalRow) => {
    setPreparandoPdf(nota.id);
    try {
      const endpoint = abaNotas === 'nfce' ? 'danfce' : 'danfe';
      const qs = abaNotas === 'nfce' ? '?formato=tradicional' : '';
      const blob = await baixarBlobAutenticado(`/api/${abaNotas}/${nota.id}/${endpoint}${qs}`);
      const url = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${abaNotas === 'nfce' ? 'NFCe' : 'NFe'}_${String(nota.numero).padStart(6, '0')}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.erro('Erro ao baixar PDF: ' + (e as Error).message);
    } finally {
      setPreparandoPdf(null);
    }
  }, [aba, toast]);

  // Comprovante nao fiscal (DA-290) — chaveia por orcamento_id (Caminho B), nao pela nota:
  // o documento e montado do orcamento/venda, entao independe do status da NF.
  const abrirComprovante = useCallback((nota: NotaFiscalRow) => {
    if (!nota.orcamento_id) return;
    setComprovanteInfo({
      orcamentoId: nota.orcamento_id,
      clienteNome: nota.cliente_razao || nota.cliente_nome,
      clienteWhatsapp: nota.cliente_whatsapp,
      valorTotal: nota.valor_total,
      orcamentoNumero: nota.orcamento_numero,
      notaAutorizada: nota.status === 'autorizada',
    });
  }, []);

  const consultarSefaz = useCallback(async (nota: NotaFiscalRow) => {
    if (!nota.chave) {
      toast.erro('Sem chave — nota nunca foi enviada a SEFAZ');
      return;
    }
    setConsultando(nota.id);
    try {
      const data = await api.post<ConsultaSefazData>(`/api/${abaNotas}/${nota.id}/consultar-sefaz`);
      const tipoLabel = abaNotas === 'nfce' ? 'NFC-e' : 'NF-e';
      setModalConsulta({ nota, data, tipoLabel });
    } catch (e) {
      toast.erro('Erro ao consultar SEFAZ: ' + (e as Error).message);
    } finally {
      setConsultando(null);
    }
  }, [aba, toast]);

  const reemitir = useCallback(async (nota: NotaFiscalRow) => {
    if (!nota.orcamento_id) {
      toast.erro('Nota sem orcamento — nao e possivel reemitir');
      return;
    }
    const tipoLabel = abaNotas === 'nfce' ? 'NFC-e' : 'NF-e';
    if (!window.confirm(
      `Reemitir ${tipoLabel} para o orcamento ${nota.orcamento_numero ?? '#' + nota.orcamento_id}?\n\n` +
      `O proximo numero da serie sera usado. A tentativa rejeitada anterior fica no historico.`
    )) return;
    try {
      const data = await api.post<{ status?: string; numero?: number; protocolo?: string; cStat?: string; xMotivo?: string }>(
        `/api/${abaNotas}/emitir/${nota.orcamento_id}`,
      );
      if (data.status === 'autorizada' || data.cStat === '100' || data.cStat === '150') {
        toast.sucesso(`${tipoLabel} ${data.numero ?? ''} autorizada — protocolo ${data.protocolo ?? ''}`);
      } else {
        toast.erro(`${tipoLabel} nao autorizada — cStat ${data.cStat}: ${data.xMotivo}`);
      }
      void carregar();
    } catch (e) {
      toast.erro('Erro ao reemitir: ' + (e as Error).message);
    }
  }, [abaNotas, carregar, toast]);

  // Troca de modelo: cancela a NFC-e (Consumidor) e emite NF-e (A4) do mesmo
  // orcamento — cliente/itens/valores/vendedor preservados (moram na venda).
  const trocarParaNfe = useCallback(async (nota: NotaFiscalRow) => {
    if (!nota.orcamento_id) {
      toast.erro('Nota sem orcamento — nao da pra trocar de modelo');
      return;
    }
    if (!window.confirm(
      `Trocar a NFC-e ${String(nota.numero).padStart(6, '0')} (Consumidor) para NF-e (A4)?\n\n` +
      `A NFC-e sera CANCELADA na SEFAZ e uma NF-e A4 sera emitida no lugar, com o mesmo cliente, itens, valores e vendedor.\n\n` +
      `So funciona dentro do prazo de cancelamento (30 min). Fora do prazo, vira caso pro contador.`
    )) return;
    setTrocando(nota.id);
    try {
      const data = await api.post<{ numero?: number; protocolo?: string }>(
        `/api/nfe/trocar-de-nfce/${nota.id}`,
      );
      toast.sucesso(
        `Trocado! NF-e A4 ${String(data.numero ?? '').padStart(6, '0')} autorizada` +
        `${data.protocolo ? ` — protocolo ${data.protocolo}` : ''}. A NFC-e foi cancelada.`,
      );
      void carregar();
    } catch (e) {
      const err = e as ApiError;
      const corpo = err?.corpo as { cliente_id?: number | null; faltam?: string[] } | undefined;
      // Cliente incompleto pra NF-e (422): nada foi cancelado — abre o cadastro pra completar.
      if (err?.status === 422 && corpo?.faltam) {
        const faltam = corpo.faltam.join(', ');
        if (corpo.cliente_id) {
          const ir = window.confirm(
            `Nao da pra emitir a NF-e (A4) ainda: o cadastro do cliente esta faltando ${faltam}.\n\n` +
            `A NFC-e NAO foi cancelada. Quer abrir o cadastro do cliente agora pra completar?`,
          );
          if (ir) navigate(`/cadastros/clientes?abrir=${corpo.cliente_id}`);
        } else {
          toast.erro(`Nao da pra trocar: ${faltam}. A NFC-e nao foi cancelada.`);
        }
        return;
      }
      toast.erro(err?.message || 'Erro ao trocar de modelo');
    } finally {
      setTrocando(null);
    }
  }, [carregar, navigate, toast]);

  const podeCancelarNota = useCallback((nota: NotaFiscalRow): boolean => {
    if (nota.origem === 'legacy') return false;
    if (!podeCancelar) return false;
    if (nota.status !== 'autorizada') return false;
    // Copia unica do prazo (compartilhada com a mensagem de recusa do backend).
    const limite = PRAZO_CANCELAMENTO_MS[abaNotas === 'nfce' ? 'nfce' : 'nfe'];
    const dt = parseSPDate(nota.emitido_em);
    if (!dt) return false;
    return Date.now() - dt.getTime() < limite;
  }, [abaNotas, podeCancelar]);

  // Le state via refs pra manter callback estavel (memo da NotaFiscalRow nao quebra)
  const toggleExpand = useCallback(async (n: NotaFiscalRow) => {
    const jaExpandido = expandedIdRef.current === n.id;
    setExpandedId(jaExpandido ? null : n.id);
    if (jaExpandido) return;
    if (itensCacheRef.current[n.id]) return;
    if (!n.orcamento_id) {
      setItensCache((p) => ({ ...p, [n.id]: [] }));
      return;
    }
    setLoadingItens(n.id);
    try {
      const itens = await api.get<OrcamentoItem[]>(`/api/orcamentos/${n.orcamento_id}/itens`);
      setItensCache((p) => ({ ...p, [n.id]: itens || [] }));
    } catch {
      setItensCache((p) => ({ ...p, [n.id]: [] }));
    } finally {
      setLoadingItens(null);
    }
  }, []);

  const clienteLabel = useCallback((n: NotaFiscalRow): string => {
    // [DA-559] A devolucao de compra nao tem cliente — o destinatario e' o FORNECEDOR. Sem esta
    // linha a coluna mostraria "—" justo na informacao que identifica a nota.
    if (n.fornecedor_nome) return n.fornecedor_nome;
    return n.cliente_razao || n.cliente_nome || (abaNotas === 'nfce' ? 'Consumidor' : '—');
  }, [abaNotas]);

  const onDetalhes = useCallback((n: NotaFiscalRow) => setModalDetalhes(n), []);
  const onWhatsapp = useCallback((n: NotaFiscalRow) => setModalWhatsapp(n), []);
  const onCancelar = useCallback((n: NotaFiscalRow) => setModalCancelar(n), []);
  const onDevolver = useCallback((n: NotaFiscalRow) => setModalDevolver(n), []);

  // Navegacao devolucao <-> nota original (auditoria de 2 lados).
  // Da devolucao: abre a nota de venda original filtrando pelo numero (janela ampla
  // pra achar mesmo se for antiga). Da venda devolvida: pula pra aba Devolucoes.
  // A origem pode ser NFC-e ([DA-440]) — ai o destino e a OUTRA aba, senao o filtro
  // cai na aba errada e a tela diz "nenhuma nota" sobre uma nota que existe.
  const onAbrirOriginal = useCallback((n: NotaFiscalRow) => {
    if (!n.ref_numero) return;
    // [DA-559] Devolucao de COMPRA: a nota original e' do FORNECEDOR e nao mora em nenhuma aba
    // desta tela — mora em /notas-entrada. Filtrar por numero aqui devolveria "nenhuma nota".
    if (n.ref_modelo === 'nota_entrada') {
      navigate(`/notas-entrada?nota=${n.nota_entrada_origem_id}`);
      return;
    }
    setDataInicio(diasAtras(3650));
    setDataFim(hoje());
    setStatus('');
    setChave('');
    setNumero(String(n.ref_numero));
    setAba(n.ref_modelo === 'nfce' ? 'nfce' : 'nfe');
  }, [navigate]);
  const onVerDevolucao = useCallback((_n: NotaFiscalRow) => {
    setAba('devolucoes');
  }, []);

  // Devolucao fiscal: NF-e mod 55 (aba 'nfe') ou NFC-e mod 65 (aba 'nfce', [DA-440]),
  // admin/gerente, nota de VENDA autorizada que ainda nao foi devolvida. SEM trava de
  // prazo — esse e o ponto do recurso (o cancelamento normal so vale 24h na NF-e e 30
  // min na NFC-e; a devolucao serve justamente fora dele).
  const podeDevolverNota = useCallback((nota: NotaFiscalRow): boolean => {
    if (abaNotas !== 'nfe' && abaNotas !== 'nfce') return false;
    if (nota.origem === 'legacy') return false;
    if (!ehAdmin) return false;
    if (nota.status !== 'autorizada') return false;
    if (nota.tipo_nota === 'devolucao') return false;
    if (nota.ja_devolvida) return false;
    // A listagem da NFC-e nao traz ja_devolvida — ela devolve o numero direto.
    if (nota.devolucao_numero) return false;
    return true;
  }, [abaNotas, ehAdmin]);

  // Orcamentos cuja pendencia ja foi resolvida por uma reemissao autorizada
  // (mesmo orcamento_id na lista carregada). Usado pra esconder o botao Reemitir
  // em linhas com status erro/rejeitada que ja tem uma nota OK no historico.
  const orcamentosComAutorizada = useMemo(() => {
    const s = new Set<number>();
    for (const n of lista) {
      if (n.status === 'autorizada' && n.orcamento_id) s.add(n.orcamento_id);
    }
    return s;
  }, [lista]);

  // Linhas virtualizadas: cada nota e 1 row; se expandida, vira 2 rows (nota + expansao)
  type VirtRow =
    | { kind: 'nota'; n: NotaFiscalRow }
    | { kind: 'exp'; n: NotaFiscalRow };
  const virtRows = useMemo<VirtRow[]>(() => {
    const out: VirtRow[] = [];
    for (const n of lista) {
      out.push({ kind: 'nota', n });
      if (expandedId === n.id) out.push({ kind: 'exp', n });
    }
    return out;
  }, [lista, expandedId]);

  return (
    <div>
      <div className="pagina-header">
        <h2 className="pagina-titulo">
          <FileText size={20} style={{ display: 'inline', marginRight: 6, marginBottom: -3 }} />
          Notas Fiscais
        </h2>
        {ehAdmin && (
          <div className="pagina-acoes">
            <button
              type="button"
              className="btn-secundario"
              onClick={() => setModalInutilizarAberto(true)}
              title="Inutilizar uma faixa de numeracao na SEFAZ (notas rejeitadas/abandonadas)"
            >
              <FileX2 size={14} style={{ display: 'inline', marginRight: 4 }} />
              Inutilizar Numeracao
            </button>
          </div>
        )}
      </div>

      {/* Abas */}
      <div className="abas">
        <button
          type="button"
          className={`aba-btn ${aba === 'nfce' ? 'aba-btn-ativo' : ''}`}
          onClick={() => setAba('nfce')}
        >
          NFC-e (Consumidor)
        </button>
        <button
          type="button"
          className={`aba-btn ${aba === 'nfe' ? 'aba-btn-ativo' : ''}`}
          onClick={() => setAba('nfe')}
        >
          NF-e (Modelo 55)
        </button>
        <button
          type="button"
          className={`aba-btn ${aba === 'problemas' ? 'aba-btn-ativo' : ''}`}
          onClick={() => setAba('problemas')}
          style={qtdProblemas > 0 ? { color: '#b91c1c', fontWeight: 700 } : undefined}
        >
          Com problemas{qtdProblemas > 0 ? ` (${qtdProblemas})` : ''}
        </button>
        <button
          type="button"
          className={`aba-btn ${aba === 'devolucoes' ? 'aba-btn-ativo' : ''}`}
          onClick={() => setAba('devolucoes')}
        >
          <Undo2 size={13} style={{ display: 'inline', marginRight: 4, marginBottom: -2 }} />
          Devolucoes
        </button>
        {ehAdmin && (
          <button
            type="button"
            className={`aba-btn ${aba === 'inutilizacoes' ? 'aba-btn-ativo' : ''}`}
            onClick={() => setAba('inutilizacoes')}
          >
            <Eraser size={13} style={{ display: 'inline', marginRight: 4, marginBottom: -2 }} />
            Inutilizacoes
          </button>
        )}
        {ehAdmin && (
          <button
            type="button"
            className={`aba-btn ${aba === 'duplicadas' ? 'aba-btn-ativo' : ''}`}
            onClick={() => setAba('duplicadas')}
            style={qtdDuplicadas > 0 ? { color: '#b91c1c', fontWeight: 700 } : undefined}
          >
            <Copy size={13} style={{ display: 'inline', marginRight: 4, marginBottom: -2 }} />
            Duplicadas{qtdDuplicadas > 0 ? ` (${qtdDuplicadas})` : ''}
          </button>
        )}
      </div>

      {aba === 'problemas' && <ProblemasNotasLista />}
      {aba === 'inutilizacoes' && ehAdmin && <InutilizacoesTab />}
      {aba === 'duplicadas' && ehAdmin && <DuplicadasTab onContagem={setQtdDuplicadas} />}

      {aba !== 'problemas' && aba !== 'inutilizacoes' && aba !== 'duplicadas' && (<>
      {aba === 'devolucoes' && (
        <div style={{ fontSize: 12, color: 'var(--text-muted)', background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 8, padding: '8px 12px', marginBottom: 12 }}>
          {/* [DA-559] A aba passou a ter DUAS naturezas que andam pra lados opostos, e o texto
              antigo descrevia so uma — lido na linha da devolucao ao fornecedor, ele mentia em
              todas as palavras: nao e nota de entrada, nao desfaz venda, e a natureza e outra. */}
          Duas naturezas dividem esta aba, e elas andam pra lados opostos.{' '}
          <strong style={{ color: '#7c3aed' }}>DEVOLUCAO</strong> = nota de <strong>entrada</strong> que
          desfaz uma <strong>venda</strong> (natureza "Devolucao de venda"); a mercadoria <strong>volta</strong> pro
          estoque e o "ref." abre a venda original.{' '}
          <strong style={{ color: '#b45309' }}>DEVOLUCAO AO FORNECEDOR</strong> = nota de <strong>saida</strong> que
          devolve uma <strong>compra</strong> (natureza "Devolucao de compra"); a mercadoria <strong>sai</strong> do
          estoque, a coluna Cliente traz o <strong>fornecedor</strong> e o "ref." abre a nota de entrada dele.
        </div>
      )}
      {aba !== 'devolucoes' && (<>
      {/* Filtros */}
      <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 8, padding: 12, marginBottom: 12, display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: 12 }}>
        <div>
          <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 4 }}>De</label>
          <input
            type="date"
            value={dataInicio}
            onChange={(e) => setDataInicio(e.target.value)}
            style={{ padding: '6px 8px', border: '1px solid var(--input-border)', borderRadius: 4, fontSize: 13 }}
          />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 4 }}>Ate</label>
          <input
            type="date"
            value={dataFim}
            onChange={(e) => setDataFim(e.target.value)}
            style={{ padding: '6px 8px', border: '1px solid var(--input-border)', borderRadius: 4, fontSize: 13 }}
          />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 4 }}>Status</label>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            style={{ padding: '6px 8px', border: '1px solid var(--input-border)', borderRadius: 4, fontSize: 13 }}
          >
            <option value="">Todos</option>
            <option value="autorizada">Autorizada</option>
            <option value="cancelada">Cancelada</option>
            <option value="rejeitada">Rejeitada</option>
            <option value="erro_comunicacao">Erro</option>
          </select>
        </div>
        <div>
          <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 4 }}>Numero</label>
          <input
            type="text"
            inputMode="numeric"
            value={numero}
            onChange={(e) => setNumero(e.target.value.replace(/\D/g, ''))}
            placeholder="Ex: 123"
            style={{ padding: '6px 8px', border: '1px solid var(--input-border)', borderRadius: 4, fontSize: 13, width: 110 }}
          />
        </div>
        <div style={{ flex: 1, minWidth: 220 }}>
          <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 4 }}>Chave (44 digitos)</label>
          <div style={{ position: 'relative' }}>
            <Search size={14} style={{ position: 'absolute', left: 8, top: 9, color: 'var(--text-muted)' }} />
            <input
              type="text"
              value={chave}
              onChange={(e) => setChave(e.target.value)}
              placeholder="Total ou parcial"
              style={{ width: '100%', padding: '6px 8px 6px 28px', border: '1px solid var(--input-border)', borderRadius: 4, fontSize: 13 }}
            />
          </div>
        </div>
        <button
          type="button"
          onClick={limparFiltros}
          style={{ padding: '6px 12px', border: '1px solid var(--border)', borderRadius: 4, fontSize: 13, background: 'transparent', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 }}
        >
          <X size={14} /> Limpar
        </button>
      </div>
      </>)}

      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 6 }}>
        {loading ? 'Carregando...' : `${lista.length} ${aba === 'devolucoes' ? 'devolucao(oes)' : 'nota(s)'}`}
      </div>

      {/* Tabela virtualizada — so renderiza ~30 linhas visiveis mesmo com 500+ notas */}
      {!loading && lista.length === 0 ? (
        <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)', border: '1px solid var(--border)', borderRadius: 8 }}>
          {aba === 'devolucoes' ? 'Nenhuma devolucao emitida ainda' : 'Nenhuma nota encontrada'}
        </div>
      ) : (
        <TableVirtuoso
          style={{ height: 'calc(100vh - 320px)', minHeight: 400, border: '1px solid var(--border)', borderRadius: 8 }}
          data={virtRows}
          components={{
            Table: (props) => <table {...props} className="tabela" style={{ ...props.style, width: '100%', borderCollapse: 'collapse' }} />,
          }}
          // O fundo vai INLINE porque o thead do TableVirtuoso e sticky: sem cor opaca as
          // linhas passariam por baixo. Tem que ser --grid-header-bg (navy), que e o par de
          // --grid-header-fg (branco) que a regra .tabela th aplica na letra — com --card-bg
          // aqui o texto ficava branco no branco e sumia.
          fixedHeaderContent={() => (
            <tr style={{ background: 'var(--grid-header-bg)' }}>
              <th style={{ width: 32, background: 'var(--grid-header-bg)' }}></th>
              <th style={{ width: 120, background: 'var(--grid-header-bg)' }}>Numero / Serie</th>
              <th style={{ width: 150, background: 'var(--grid-header-bg)' }}>Emissao</th>
              <th style={{ width: 60, background: 'var(--grid-header-bg)' }}>Cod</th>
              {/* [DA-559] Na aba Devolucoes a coluna carrega cliente OU fornecedor (a devolucao de
                  compra tem destinatario, nao comprador). "Cliente" sozinho rotula errado metade
                  das linhas. */}
              <th style={{ background: 'var(--grid-header-bg)' }}>{aba === 'devolucoes' ? 'Cliente / Fornecedor' : 'Cliente'}</th>
              <th style={{ textAlign: 'right', width: 110, background: 'var(--grid-header-bg)' }}>Valor</th>
              <th style={{ width: 130, background: 'var(--grid-header-bg)' }}>Status</th>
              <th style={{ width: 130, background: 'var(--grid-header-bg)' }}>Vendedor</th>
              <th style={{ textAlign: 'right', width: 200, background: 'var(--grid-header-bg)' }}>Acoes</th>
            </tr>
          )}
          itemContent={(_idx, row) => {
            if (row.kind === 'nota') {
              return (
                <NotaFiscalCells
                  nota={row.n}
                  isExpanded={expandedId === row.n.id}
                  isLoadingItens={loadingItens === row.n.id}
                  isPreparandoDanfe={preparandoDanfe === row.n.id}
                  isPreparandoPdf={preparandoPdf === row.n.id}
                  isConsultando={consultando === row.n.id}
                  isTrocando={trocando === row.n.id}
                  podeCancelar={podeCancelarNota(row.n)}
                  podeDevolver={podeDevolverNota(row.n)}
                  jaResolvida={!!row.n.orcamento_id && orcamentosComAutorizada.has(row.n.orcamento_id)}
                  ehNfce={abaNotas === 'nfce'}
                  clienteNomeFinal={clienteLabel(row.n)}
                  onToggle={toggleExpand}
                  onDetalhes={onDetalhes}
                  onDanfe={verDanfe}
                  onAbrirMenuFormato={(n, rect) => setMenuFormato({ nota: n, rect })}
                  onWhatsapp={onWhatsapp}
                  onPdf={baixarPdf}
                  onComprovante={abrirComprovante}
                  onSefaz={consultarSefaz}
                  onReemitir={reemitir}
                  onTrocarParaNfe={trocarParaNfe}
                  onCancelar={onCancelar}
                  onDevolver={onDevolver}
                  onAbrirOriginal={onAbrirOriginal}
                  onVerDevolucao={onVerDevolucao}
                />
              );
            }
            return <NotaFiscalExpansaoCell itens={itensCache[row.n.id]} ehLegacy={row.n.origem === 'legacy'} />;
          }}
        />
      )}

      {modalDetalhes && (
        <ModalDetalhesNota nota={modalDetalhes} tipo={abaNotas} onClose={() => setModalDetalhes(null)} />
      )}
      {modalCancelar && (
        <ModalCancelarNota
          nota={modalCancelar}
          tipo={abaNotas}
          onClose={() => setModalCancelar(null)}
          onSuccess={() => { setModalCancelar(null); void carregar(); }}
        />
      )}
      {modalConsulta && (
        <ModalConsultaSefaz
          nota={modalConsulta.nota}
          data={modalConsulta.data}
          tipoLabel={modalConsulta.tipoLabel}
          onClose={() => setModalConsulta(null)}
        />
      )}
      {/* Consumidor (venda de balcao, sem cliente real) nao tem contatos cadastrados: cai no modo
          de 1 contato, que permite buscar/digitar um numero avulso. Cliente nomeado segue no modo
          multi (todos os contatos dele, envio em massa). */}
      {modalWhatsapp && (
        <ModalEnviarWhatsAppNF
          tipo={abaNotas}
          id={modalWhatsapp.id}
          numero={modalWhatsapp.numero}
          aviso={
            modalWhatsapp.cliente_whatsapp
              ? undefined
              : modalWhatsapp.cliente_nome && modalWhatsapp.cliente_nome !== 'Consumidor'
                ? `${modalWhatsapp.cliente_nome} nao possui WhatsApp cadastrado. Selecione um contato abaixo.`
                : 'Selecione o contato para envio.'
          }
          preSelecionarTelefone={modalWhatsapp.cliente_whatsapp}
          clienteId={modalWhatsapp.cliente_nome && modalWhatsapp.cliente_nome !== 'Consumidor' ? modalWhatsapp.cliente_id : null}
          clienteNome={modalWhatsapp.cliente_nome ?? undefined}
          onClose={(enviado) => {
            setModalWhatsapp(null);
            if (enviado) toast.sucesso('Nota enviada por WhatsApp');
          }}
        />
      )}
      {comprovanteInfo && (
        <ModalComprovanteVenda info={comprovanteInfo} onFechar={() => setComprovanteInfo(null)} />
      )}
      {modalDevolver && (
        <ModalDevolverNota
          nota={modalDevolver}
          modelo={abaNotas}
          onClose={() => setModalDevolver(null)}
          onSuccess={(r) => {
            // Mesmo desfecho das duas portas ([DA-443]): a acao e a mesma, o resultado tambem.
            setResultadoDevolucao({
              resultado: r,
              origemRotulo: abaNotas === 'nfce' ? 'NFC-e' : 'NF-e',
              origemNumero: modalDevolver.numero,
            });
            setModalDevolver(null);
            void carregar();
          }}
        />
      )}
      </>)}
      {resultadoDevolucao && (
        <ModalResultadoDevolucao
          resultado={resultadoDevolucao.resultado}
          origemRotulo={resultadoDevolucao.origemRotulo}
          origemNumero={resultadoDevolucao.origemNumero}
          onFechar={() => setResultadoDevolucao(null)}
        />
      )}
      {modalInutilizarAberto && (
        <ModalInutilizarNumeracao onClose={() => setModalInutilizarAberto(false)} />
      )}

      {/* Menu de formato do cupom (NFC-e) — ancorado no caret da linha */}
      {menuFormato && (() => {
        const r = menuFormato.rect;
        const alturaMenu = 84;
        const abrirCima = window.innerHeight - r.bottom < alturaMenu && r.top > alturaMenu;
        const top = abrirCima ? r.top - 4 - alturaMenu : r.bottom + 4;
        const left = Math.max(8, r.right - 210);
        return (
          <>
            <div onClick={() => setMenuFormato(null)} style={{ position: 'fixed', inset: 0, zIndex: 79 }} />
            <div
              className="dropdown-painel"
              style={{ position: 'fixed', top, left, margin: 0, zIndex: 80, minWidth: 210 }}
            >
              <button
                type="button"
                className="dropdown-item"
                onClick={() => { verDanfe(menuFormato.nota, 'cartao'); setMenuFormato(null); }}
              >
                <CreditCard size={15} />
                Maquina de Cartao (5,5cm)
              </button>
              <button
                type="button"
                className="dropdown-item"
                onClick={() => { verDanfe(menuFormato.nota, 'tradicional'); setMenuFormato(null); }}
              >
                <Receipt size={15} />
                Tradicional (8cm)
              </button>
            </div>
          </>
        );
      })()}
    </div>
  );
}

// =====================================================
// NotaFiscalCells — so as <td>s da linha principal (sem <tr>, pq o TableVirtuoso ja injeta).
// Memoizada pra nao re-renderizar 30+ linhas quando 1 muda.
// =====================================================
// Vaga fixa de acao — o que mantem os icones ALINHADOS na vertical entre as linhas.
// A faixa e alinhada a direita e cada linha tem um jogo diferente de acoes (cancelar so
// dentro do prazo, devolver so fora dele, trocar so em NFC-e autorizada...), entao sem a
// vaga uma linha com menos botoes empurra TODOS os seus icones e nenhuma coluna bate com a
// de cima. Largura = a do `.btn-icon` MEDIDA na tela (34px) — a vaga que existia aqui antes
// era de 26px e desalinhava 8px calados. Ver [[feedback_validar_estilo_computed]].
const LARGURA_ACAO = 34;
function VagaAcao() {
  return <span aria-hidden="true" style={{ display: 'inline-block', width: LARGURA_ACAO, flex: '0 0 auto' }} />;
}

interface NotaFiscalCellsProps {
  nota: NotaFiscalRow;
  isExpanded: boolean;
  isLoadingItens: boolean;
  isPreparandoDanfe: boolean;
  isPreparandoPdf: boolean;
  isConsultando: boolean;
  isTrocando: boolean;
  podeCancelar: boolean;
  podeDevolver: boolean;
  jaResolvida: boolean;
  ehNfce: boolean;
  clienteNomeFinal: string;
  onToggle: (n: NotaFiscalRow) => void;
  onDetalhes: (n: NotaFiscalRow) => void;
  onDanfe: (n: NotaFiscalRow, formato?: 'cartao' | 'tradicional') => void;
  onAbrirMenuFormato: (n: NotaFiscalRow, rect: DOMRect) => void;
  onWhatsapp: (n: NotaFiscalRow) => void;
  onPdf: (n: NotaFiscalRow) => void;
  onComprovante: (n: NotaFiscalRow) => void;
  onSefaz: (n: NotaFiscalRow) => void;
  onReemitir: (n: NotaFiscalRow) => void;
  onTrocarParaNfe: (n: NotaFiscalRow) => void;
  onCancelar: (n: NotaFiscalRow) => void;
  onDevolver: (n: NotaFiscalRow) => void;
  onAbrirOriginal: (n: NotaFiscalRow) => void;
  onVerDevolucao: (n: NotaFiscalRow) => void;
}

const NotaFiscalCells = memo(function NotaFiscalCells({
  nota: n,
  isExpanded,
  isLoadingItens,
  isPreparandoDanfe,
  isPreparandoPdf,
  isConsultando,
  isTrocando,
  podeCancelar,
  podeDevolver,
  jaResolvida,
  ehNfce,
  clienteNomeFinal,
  onToggle,
  onDetalhes,
  onDanfe,
  onAbrirMenuFormato,
  onWhatsapp,
  onPdf,
  onComprovante,
  onSefaz,
  onReemitir,
  onTrocarParaNfe,
  onCancelar,
  onDevolver,
  onAbrirOriginal,
  onVerDevolucao,
}: NotaFiscalCellsProps) {
  const st = STATUS_LABEL[n.status] || { label: n.status, cls: 'badge-cinza' };
  const podeReemitir = n.origem !== 'legacy' && (n.status === 'rejeitada' || n.status === 'erro_comunicacao') && !!n.orcamento_id && !jaResolvida;
  const ehLegacy = n.origem === 'legacy';
  // Comprovante nao fiscal (DA-290) em TODA nota, inclusive autorizada. Na autorizada ele e
  // redundante — o cupom fiscal e um documento melhor — e o Ruan pediu assim mesmo, com o
  // argumento que vale mais que a redundancia: ferramenta que nao aparece e esquecida (em
  // 07/08 ele pediu pra construir este comprovante sem saber que ele ja existia, escondido
  // num botao apagado da aba "Com problemas"). Fora: nota legada (nao tem orcamento pra
  // montar o documento) e devolucao (nota de ENTRADA — nao ha venda pra comprovar).
  const podeComprovante = !ehLegacy && !!n.orcamento_id && n.tipo_nota !== 'devolucao';
  // Trocar para NF-e (A4): so na aba NFC-e, nota autorizada, nao-legada, com orcamento,
  // e quem pode cancelar (admin/gerente dentro do prazo — mesma trava do Cancelar).
  const podeTrocar = ehNfce && !ehLegacy && n.status === 'autorizada' && !!n.orcamento_id && podeCancelar;
  const danfeDisabled = ehLegacy || isPreparandoDanfe || (n.status !== 'autorizada' && n.status !== 'cancelada');
  return (
    <>
      <td>
        <button
          type="button"
          onClick={() => onToggle(n)}
          className="btn-icon"
          title="Ver itens"
        >
          {isLoadingItens ? <Loader2 size={14} className="animate-spin" /> : isExpanded ? <Minus size={14} /> : <Plus size={14} />}
        </button>
      </td>
      <td>
        <div style={{ fontWeight: 600 }}>{String(n.numero).padStart(6, '0')}</div>
        <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
          Serie {n.serie}
          {String(n.ambiente) === '2' && <span style={{ color: '#d97706', marginLeft: 6 }}>(homolog)</span>}
          {ehLegacy && <span style={{ color: '#6b7280', marginLeft: 6, fontStyle: 'italic' }}>(legacy)</span>}
        </div>
        {/* [DA-559] Duas devolucoes MUITO diferentes dividem o mesmo tipo_nota, e a diferenca e' o
            sentido: a de venda ENTRA mercadoria, a de compra SAI. O selo diz qual, senao a aba
            Devolucoes mistura as duas sem nada que as separe. */}
        {n.tipo_nota === 'devolucao' && (
          <div style={{ marginTop: 2, display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 10, fontWeight: 700, color: '#fff', background: n.ref_modelo === 'nota_entrada' ? '#b45309' : '#7c3aed', borderRadius: 4, padding: '1px 5px' }}>
            <Undo2 size={10} /> {n.ref_modelo === 'nota_entrada' ? 'DEVOLUCAO AO FORNECEDOR' : 'DEVOLUCAO'}
          </div>
        )}
        {n.tipo_nota === 'devolucao' && n.ref_numero ? (
          <div style={{ marginTop: 2 }}>
            <button
              type="button"
              onClick={() => onAbrirOriginal(n)}
              title={n.ref_modelo === 'nota_entrada'
                ? 'Abrir a nota de entrada do fornecedor que esta sendo devolvida'
                : `Abrir a ${n.ref_modelo === 'nfce' ? 'NFC-e' : 'NF-e'} de venda original`}
              style={{ background: 'transparent', border: 0, padding: 0, fontSize: 11, color: 'var(--primary)', cursor: 'pointer', textDecoration: 'underline' }}
            >
              {n.ref_modelo === 'nota_entrada'
                ? `ref. entrada ${String(n.ref_numero)}`
                : `ref. ${n.ref_modelo === 'nfce' ? 'NFC-e' : 'NF'} ${String(n.ref_numero).padStart(6, '0')}`}
            </button>
          </div>
        ) : null}
        {/* NF-e traz ja_devolvida; a NFC-e traz o numero direto ([DA-440]) — os dois
            acendem o mesmo selo, que e o mesmo FATO ("esta nota foi desfeita"). */}
        {n.tipo_nota !== 'devolucao' && (n.ja_devolvida || n.devolucao_numero) ? (
          <div style={{ marginTop: 2 }}>
            <button
              type="button"
              onClick={() => onVerDevolucao(n)}
              title="Esta venda foi desfeita por uma devolucao — ver na aba Devolucoes"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 10, fontWeight: 700, color: '#7c3aed', background: 'transparent', border: '1px solid #7c3aed', borderRadius: 4, padding: '1px 5px', cursor: 'pointer' }}
            >
              <Undo2 size={10} /> Devolvida{n.devolucao_numero ? ` (nº ${String(n.devolucao_numero).padStart(6, '0')})` : ''}
            </button>
          </div>
        ) : null}
      </td>
      <td>{formatDataHoraSP(n.emitido_em)}</td>
      <td className="text-gray-500 font-bold no-underline">
        {n.cliente_codigo ? String(n.cliente_codigo).padStart(4, '0') : ''}
      </td>
      <td>
        <div>{clienteNomeFinal}</div>
        {n.cliente_doc && (
          <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{formatCpfCnpj(n.cliente_doc)}</div>
        )}
      </td>
      <td style={{ textAlign: 'right' }}>
        <div style={{ fontWeight: 600 }}>{formatMoeda(n.valor_total)}</div>
        {n.forma_pagamento && (
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{n.forma_pagamento}</div>
        )}
      </td>
      <td>
        <span className={st.cls}>{st.label}</span>
        {n.cancelado_em && (
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
            em {parseSPDate(n.cancelado_em)?.toLocaleDateString('pt-BR') ?? ''}
          </div>
        )}
      </td>
      <td style={{ color: 'var(--text-muted)' }}>{n.vendedor_nome || ''}</td>
      <td>
        <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
          <button type="button" className="btn-icon" title="Detalhes" onClick={() => onDetalhes(n)}>
            <Info size={14} />
          </button>
          {ehNfce ? (
            <span style={{ display: 'inline-flex', alignItems: 'stretch' }}>
              <button
                type="button"
                className="btn-icon"
                title={ehLegacy ? 'Imprimir cupom (nota legada — consulte pelo ControleFC antigo)' : 'Imprimir cupom (Maquina de Cartao)'}
                disabled={danfeDisabled}
                onClick={() => onDanfe(n, 'cartao')}
              >
                {isPreparandoDanfe ? <Loader2 size={14} className="animate-spin" /> : <Printer size={14} />}
              </button>
              <button
                type="button"
                className="btn-icon"
                title="Escolher formato do cupom"
                style={{ minWidth: 16, padding: '0 1px', marginLeft: -3 }}
                disabled={danfeDisabled}
                onClick={(ev) => onAbrirMenuFormato(n, ev.currentTarget.getBoundingClientRect())}
              >
                <ChevronDown size={11} />
              </button>
            </span>
          ) : (
            <button
              type="button"
              className="btn-icon"
              title={ehLegacy ? 'Imprimir DANFE (nota legada — consulte pelo ControleFC antigo)' : 'Imprimir DANFE'}
              disabled={danfeDisabled}
              onClick={() => onDanfe(n)}
            >
              {isPreparandoDanfe ? <Loader2 size={14} className="animate-spin" /> : <Printer size={14} />}
            </button>
          )}
          <button
            type="button"
            className="btn-icon"
            title={ehLegacy ? 'Nota legada — sem envio por WhatsApp' : (n.status === 'autorizada' ? 'Enviar por WhatsApp' : 'So envia notas autorizadas')}
            disabled={ehLegacy || n.status !== 'autorizada'}
            onClick={() => onWhatsapp(n)}
          >
            <MessageCircle size={14} />
          </button>
          <button
            type="button"
            className="btn-icon"
            title={ehLegacy ? 'Baixar PDF (nota legada — consulte pelo ControleFC antigo)' : (ehNfce ? 'Baixar cupom em PDF' : 'Baixar DANFE em PDF')}
            disabled={danfeDisabled || isPreparandoPdf}
            onClick={() => onPdf(n)}
          >
            {isPreparandoPdf ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
          </button>
          {podeComprovante ? (
            <button
              type="button"
              className="btn-icon"
              title="Comprovante nao fiscal — documento SEM VALOR FISCAL pro cliente (imprimir / baixar / WhatsApp)"
              onClick={() => onComprovante(n)}
              style={{ color: '#f97316' }}
            >
              <Receipt size={14} />
            </button>
          ) : <VagaAcao />}
          <button
            type="button"
            className="btn-icon"
            title={ehLegacy ? 'Consulta SEFAZ (nota legada)' : (n.chave ? 'Consultar status na SEFAZ' : 'Sem chave')}
            disabled={ehLegacy || !n.chave || isConsultando}
            onClick={() => onSefaz(n)}
          >
            {isConsultando ? <Loader2 size={14} className="animate-spin" /> : <Shield size={14} />}
          </button>
          {/* "Trocar para NF-e" so existe na aba NFC-e — na aba NF-e nem a vaga se justifica
              (a coluna inteira ficaria 34px mais larga por uma acao que nunca aparece la). */}
          {ehNfce && (podeTrocar ? (
            <button
              type="button"
              className="btn-icon"
              title="Trocar para NF-e (A4) — cancela esta NFC-e e emite uma NF-e A4 no lugar"
              disabled={isTrocando}
              onClick={() => onTrocarParaNfe(n)}
              style={{ color: '#4f46e5' }}
            >
              {isTrocando ? <Loader2 size={14} className="animate-spin" /> : <FileUp size={14} />}
            </button>
          ) : <VagaAcao />)}
          {podeReemitir ? (
            <button type="button" className="btn-icon" title="Reemitir" onClick={() => onReemitir(n)} style={{ color: '#d97706' }}>
              <RefreshCw size={14} />
            </button>
          ) : <VagaAcao />}
          {podeDevolver ? (
            <button type="button" className="btn-icon" title="Emitir devolucao (desfaz a nota fora do prazo de cancelamento)" onClick={() => onDevolver(n)} style={{ color: '#7c3aed' }}>
              <Undo2 size={14} />
            </button>
          ) : <VagaAcao />}
          {podeCancelar ? (
            <button type="button" className="btn-icon" title="Cancelar" onClick={() => onCancelar(n)} style={{ color: '#dc2626' }}>
              <Ban size={14} />
            </button>
          ) : <VagaAcao />}
        </div>
      </td>
    </>
  );
});

// =====================================================
// NotaFiscalExpansaoCell — celula colSpan da linha de expansao (itens da nota)
// =====================================================
const NotaFiscalExpansaoCell = memo(function NotaFiscalExpansaoCell({ itens, ehLegacy }: { itens: OrcamentoItem[] | undefined; ehLegacy: boolean }) {
  return (
    <td colSpan={9} style={{ padding: '8px 16px', background: 'rgba(59, 130, 246, 0.04)' }}>
      {!itens ? (
        <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Carregando...</span>
      ) : itens.length === 0 ? (
        <span style={{ fontSize: 12, color: 'var(--text-muted)', fontStyle: ehLegacy ? 'italic' : 'normal' }}>
          {ehLegacy ? 'Nota legada — itens disponíveis somente no ControleFC antigo.' : 'Sem itens'}
        </span>
      ) : (
        <table style={{ width: '100%', fontSize: 12 }}>
          <thead>
            <tr style={{ color: 'var(--text-muted)' }}>
              <th style={{ textAlign: 'center', padding: '4px 6px', width: 32 }}>#</th>
              <th style={{ textAlign: 'left', padding: '4px 6px', width: 48 }}></th>
              <th style={{ textAlign: 'left', padding: '4px 6px' }}>Cod</th>
              <th style={{ textAlign: 'left', padding: '4px 6px' }}>Produto</th>
              <th style={{ textAlign: 'left', padding: '4px 6px' }}>Un</th>
              <th style={{ textAlign: 'right', padding: '4px 6px' }}>Qtd</th>
              <th style={{ textAlign: 'right', padding: '4px 6px' }}>Unit.</th>
              <th style={{ textAlign: 'right', padding: '4px 6px' }}>Desc%</th>
              <th style={{ textAlign: 'right', padding: '4px 6px' }}>Subtotal</th>
            </tr>
          </thead>
          <tbody>
            {itens.map((it, idx) => (
              <tr key={it.id} style={{ borderTop: '1px solid var(--border)' }}>
                <td style={{ padding: '4px 6px', textAlign: 'center', color: 'var(--text-muted)' }}>{idx + 1}</td>
                <td style={{ padding: '4px 6px' }}>
                  {it.foto_url ? (
                    <FotoProduto url={it.foto_url} tam={36} />
                  ) : (
                    <div style={{ width: 36, height: 36, borderRadius: 4, background: '#f3f4f6', border: '1px solid var(--input-border, #e5e7eb)' }} />
                  )}
                </td>
                <td className="text-gray-500 font-bold no-underline" style={{ padding: '4px 6px' }}>
                  {it.codigo_interno || ''}
                </td>
                <td style={{ padding: '4px 6px' }}>{it.produto_descricao}</td>
                <td style={{ padding: '4px 6px', color: 'var(--text-muted)' }}>{it.unidade_sigla || ''}</td>
                <td style={{ padding: '4px 6px', textAlign: 'right' }}>{Number(it.quantidade).toLocaleString('pt-BR')}</td>
                <td style={{ padding: '4px 6px', textAlign: 'right' }}>{formatMoeda(it.preco_unitario)}</td>
                <td style={{ padding: '4px 6px', textAlign: 'right', color: 'var(--text-muted)' }}>
                  {Number(it.desconto_percentual || 0) > 0 ? `${Number(it.desconto_percentual).toFixed(2)}%` : '—'}
                </td>
                <td style={{ padding: '4px 6px', textAlign: 'right', fontWeight: 600 }}>{formatMoeda(it.subtotal)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </td>
  );
});
